import React, { useState, useEffect } from 'react';
import './PanditManagement-simple.css';
import apiClient from '../../api';

const PanditManagement = () => {
  const [pandits, setPandits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    fetchPandits();
  }, []);

  const fetchPandits = async () => {
    try {
      setLoading(true);

      console.log('🔝 Fetching pandits from API...');

      // For admin panel, use admin/all endpoint to get ALL pandits (including pending)
      try {
        const response = await fetch('https://pooja-path-1.onrender.com/api/pandit/admin/all/', {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('📦 Admin API response:', data);

          // Paginated response: { count, results }
          if (data.results && Array.isArray(data.results)) {
            setPandits(data.results);
            console.log(`✅ Loaded ${data.results.length} pandits from admin endpoint`);
            setLoading(false);
            return;
          } else if (Array.isArray(data)) {
            setPandits(data);
            console.log(`✅ Loaded ${data.length} pandits from admin endpoint`);
            setLoading(false);
            return;
          }
        } else {
          console.warn(`⚠️ Admin API returned status ${response.status}`);
        }
      } catch (error) {
        console.warn('⚠️ Admin API call failed:', error.message);
      }

      // Fallback to public pandits endpoint (only approved)
      try {
        const response = await fetch('https://pooja-path-1.onrender.com/api/pandit/pandits/', {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('📦 Pandits API response:', data);

          // Response is an array of pandits
          if (Array.isArray(data)) {
            setPandits(data);
            console.log(`✅ Loaded ${data.length} pandits from /api/pandit/pandits/`);
            setLoading(false);
            return;
          }
        } else {
          console.warn(`⚠️ Pandits API returned status ${response.status}`);
        }
      } catch (error) {
        console.warn('⚠️ Pandits API call failed:', error.message);
      }

      // Fallback to demo data
      const demoPandits = [
        {
          id: 1,
          name: "Pandit Rajesh Sharma",
          phone: "+91 9876543210",
          location: "Delhi",
          created_at: "2025-01-20T10:30:00Z"
        },
        {
          id: 2,
          name: "Pandit Suresh Kumar",
          phone: "+91 9876543211",
          location: "Mumbai",
          created_at: "2025-01-19T14:20:00Z"
        },
        {
          id: 3,
          name: "Pandit Mahesh Tiwari",
          phone: "+91 9876543212",
          location: "Varanasi",
          created_at: "2025-01-18T09:15:00Z"
        }
      ];

      setPandits(demoPandits);
      console.log('📋 Demo pandit data loaded');
      setLoading(false);

    } catch (error) {
      console.error('❌ Error fetching pandits:', error);
      setLoading(false);
    }
  };

  const handleEdit = async (pandit) => {
    const newName = prompt('Enter new name:', pandit.name || pandit.Pandit_name);
    if (newName === null) return;
    
    const newPhone = prompt('Enter new phone:', pandit.phone);
    if (newPhone === null) return;

    const newLocation = prompt('Enter new location:', pandit.location || pandit.Location);
    if (newLocation === null) return;
    
    const newDescription = prompt('Enter new description:', pandit.description || '');

    const updateData = {
        name: newName,
        phone: newPhone,
        location: newLocation,
        description: newDescription
    };

    try {
        const response = await apiClient.put(`/api/pandit/admin/update/${pandit.id || pandit._id}/`, updateData);
        if (response.status === 200) {
            alert('Pandit info updated successfully');
            fetchPandits();
        } else {
            alert('Failed to update pandit info');
        }
    } catch (error) {
        console.error('Error updating pandit:', error);
        alert('Error updating pandit info');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredPandits = pandits.filter(pandit =>
    (pandit.name && pandit.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (pandit.Pandit_name && pandit.Pandit_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (pandit.phone && pandit.phone.includes(searchTerm)) ||
    (pandit.location && pandit.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (pandit.Location && pandit.Location.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (pandit.description && pandit.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleCard = (panditId) => {
    setExpandedCards(prev => ({
      ...prev,
      [panditId]: !prev[panditId]
    }));
  };

  if (loading) {
    return (
      <div className="pandit-management">
        <div className="management-header">
          <h2 className="section-title">🙝 Pandit Management</h2>
          <div className="loading-message">
            ❳ Loading pandits...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pandit-management">
      <div className="management-header">
        <h2 className="section-title">🙝 Pandit Management</h2>
        <p className="section-description">
          View all registered pandits (Read-only)
        </p>
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-number">{pandits.length}</span>
          <span className="stat-label">Total Pandits</span>
        </div>
      </div>

      <div className="search-bar-container">
        <input
          type="text"
          placeholder="🔍 Search pandits by name, phone, location, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="pandits-grid">
        {filteredPandits.length === 0 && pandits.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🙏</div>
            <h3>No Pandits Found</h3>
            <p>No pandits are currently registered in the system.</p>
          </div>
        ) : filteredPandits.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No Matching Pandits Found</h3>
            <p>Try adjusting your search term to find pandits.</p>
          </div>
        ) : (
          filteredPandits.map((pandit, index) => {
            // Use a unique key combining id and index to ensure uniqueness
            const uniqueKey = pandit.id || pandit._id || `pandit-${index}`;
            const isExpanded = expandedCards[uniqueKey];
            return (
              <div key={uniqueKey} className={`pandit-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="pandit-header">
                  <div className="pandit-avatar">
                    🙏
                  </div>
                  <div className="pandit-info">
                    <h3 className="pandit-name">{pandit.name || pandit.Pandit_name}</h3>
                    {isExpanded && <span className="pandit-id">ID: {pandit.id || pandit._id}</span>}
                  </div>
                </div>

                {isExpanded && (
                  <>
                    <div className="pandit-details">
                      <div className="detail-item">
                        <span className="detail-label">📞 Phone:</span>
                        <span className="detail-value">{pandit.phone}</span>
                      </div>

                      <div className="detail-item">
                        <span className="detail-label">📍 Location:</span>
                        <span className="detail-value">{pandit.location || pandit.Location}</span>
                      </div>

                      {pandit.description && (
                        <div className="detail-item description-item">
                          <span className="detail-label">📝 Description:</span>
                          <span className="detail-value description-text">{pandit.description}</span>
                        </div>
                      )}

                      <div className="detail-item">
                        <span className="detail-label">📅 Registered:</span>
                        <span className="detail-value">{formatDate(pandit.created_at)}</span>
                      </div>
                    </div>

                    <div className="pandit-status">
                      <span className="status-badge active">✅ Active</span>
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    className="toggle-details-btn"
                    style={{ flex: 1 }}
                    onClick={() => toggleCard(uniqueKey)}
                  >
                    {isExpanded ? '▲ Show Less' : '▼ Show More'}
                  </button>
                  <button
                    className="edit-btn"
                    style={{ background: '#007bff', color: 'white', border: 'none', borderRadius: '8px', padding: '10px', cursor: 'pointer', flex: 1, fontWeight: 'bold' }}
                    onClick={() => handleEdit(pandit)}
                  >
                    ✏️ Edit Info
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PanditManagement;